import './Select.scss';
import './Select.m.scss';
import { ListMixin } from '@components/Mixin/ListMixin/ListMixin.js';
import { Field } from '@components/Component/Field/Field.js';
import { DataRecord } from '@components/DataRecord/DataRecord.js';
import { combineLatest, map, of, Subject, tap, timer } from 'rxjs';
import { debounce as RxDebounce, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { debounce, isArray, isMobile } from '@components/Utility/index.js';
import DIContainer from '@components/DependencyInjectionContainer.js';
/**
 * Class `Select` extends from the `Field` class.
 * It represents a select input field.
 */
export class Select extends ListMixin(Field) {
    get optionValueKey() { return this._optionValueKey; }
    set optionValueKey(value) {
        this.optionDataIdKey = value;
        this._propertySet('optionValueKey', value);
        if (this.staticOptionData.length) {
            this.staticOptionData.forEach((option) => {
                this.formatStaticOptionData(option);
            });
        }
        if (this.drawn && this.optionData.length) {
            this.renderOptionData(this.optionData);
        }
    }
    get inputValue() {
        return this.strValue;
    }
    get multiSelectDragToSort() { return this._multiSelectDragToSort; }
    set multiSelectDragToSort(value) {
        this._propertySet('multiSelectDragToSort', value);
        if (this.drawn) {
            this.renderMultiSelect();
        }
    }
    get multiSelectDelimiter() { return this._multiSelectDelimiter; }
    set multiSelectDelimiter(c) {
        this._propertySet('multiSelectDelimiter', c);
    }
    get allowFreeFormEntry() { return this._allowFreeFormEntry; }
    set allowFreeFormEntry(allow) { this._propertySet('allowFreeFormEntry', allow); }
    get optionDisplayKey() { return this._optionDisplayKey; }
    set optionDisplayKey(display) {
        if (display && display.indexOf(',') > -1) {
            display = display.split(',');
        }
        this._propertySet('optionDisplayKey', display);
        if (this.staticOptionData.length) {
            this.staticOptionData.forEach((option) => {
                this.formatStaticOptionData(option);
            });
        }
        if (this.drawn && this.optionData.length) {
            this.renderOptionData(this.optionData);
        }
    }
    get includeAll() { return this._includeAll; }
    set includeAll(value) {
        if (value && !this.allOption) {
            let option = {
                value: this.allValue,
                display: this.CaptionManager.getCaption('system_all_caption')
            };
            option = this.formatStaticOptionData(option);
            const id = this.addOptionId(option);
            this.allOption = option;
            this.allOptionsById[id] = option;
        }
        else if (this.allOption) {
            // we need to remove the static option
            delete this.allOptionsById[this.allOption._id];
            this.allOption = undefined;
        }
        this._propertySet('includeAll', value);
    }
    get useNativeSelect() {
        return this._useNativeSelect;
    }
    set useNativeSelect(value) {
        this._propertySet('useNativeSelect', value);
        if (this.drawn) {
            if (value) {
                this.inputElement.classList.remove('d-none');
                this.customSelectElement.classList.add('d-none');
            }
            else {
                this.customSelectElement.classList.remove('d-none');
                this.inputElement.classList.add('d-none');
            }
        }
    }
    constructor(id, rootElement, dependencies = {}) {
        super(id, rootElement, dependencies);
        this.componentName = 'Select';
        /**
         * The column that contains the primary "value" for the selected option.
         * This should match one of the columns in the database resultset.
         **/
        this._optionValueKey = 'value';
        this.showAutoCompleteNoResults = true;
        /** indicates if this control allows multi selection */
        // inherited from ListMixin.
        // multiSelect: boolean = false;
        // indicates if we will maintain the this.value property as an array of string values, or as a CSV string of values. only applicable if this.multiSelect is true. 
        this.multiSelectValueType = 'CSV';
        this._multiSelectDragToSort = false;
        this._multiSelectDelimiter = ',';
        this._allowFreeFormEntry = false;
        this.allowDeleteOptionData = false;
        // dont currently need a getter and setter as this is just for filterToggle for now.
        // get allowDeleteOptionData(){return this._allowFreeFormEntry;}
        // set allowDeleteOptionData(allow: boolean) {this._propertySet('allowFreeFormEntry', allow);}
        /**
         * The column(s) that contain the descriptive info for the row
         * ie. <company> - <customerno> would be ['company', 'customerno']
         **/
        this._optionDisplayKey = 'display';
        this.allValue = 'ALL';
        this._includeAll = false;
        this.listBoxExpanded = false;
        this._useNativeSelect = false;
        this._optionsByValue = {};
        this.renderOptionDataStart$ = new Subject();
        this.filteringUpdate$ = new Subject();
        this.rebuildOptionDataStream$ = new Subject();
        this._clickAwayEventId = '';
        this._mouseOverEventId = '';
        this.filterValue = '';
        this.hotkeys = {
            ...this.hotkeys // we are extending the hotkeys from the parent class. 
            ,
            ' ': () => {
                if (!this.useNativeSelect && !this.listBoxExpanded) {
                    this.displayListBox();
                    return true;
                }
            },
            'arrowup': () => {
                if (!this.useNativeSelect && this.listBoxExpanded) {
                    this.navigateList('up');
                    return true;
                }
            },
            'arrowdown': () => {
                if (!this.useNativeSelect && this.listBoxExpanded) {
                    this.navigateList('down');
                    return true;
                }
            },
            'enter': () => {
                if (!this.useNativeSelect && this.listBoxExpanded) {
                    this.selectActiveItem();
                    return true;
                }
            },
            'escape': () => {
                if (!this.useNativeSelect && this.listBoxExpanded) {
                    this.hideListBox();
                    this.focus();
                    return true;
                }
            }
        };
        this.optionDataIdKey = 'value';
    }
    draw() {
        this.rootElement.classList.add('s360Input'); // inherit the styles from input
        this.rootElement.classList.add('s360Select'); // any class extending select will need the styles
        super.draw();
    }
    /**
     * avoid trailing spaces (and potentially other issues we might find later) causing issues with programmatically selecting an option.
     * @param value
     * @private
     */
    sanitizeOptionDataValue(value) {
        return (value || '').trim();
    }
    addStaticOptionsToIdMap() {
        super.addStaticOptionsToIdMap();
        if (this.allOption) {
            this.allOptionsById[this.allOption._id] = this.allOption;
        }
    }
    staticOptionDataGet() {
        const data = [];
        if (this.allOption) {
            data.push(this.allOption);
        }
        return [...data, ...this.staticOptionData];
    }
    optionsStreamGet$(includeStatic = true) {
        return combineLatest([
            super.optionsStreamGet$(includeStatic),
            this.propertyGet$('includeAll')
        ]).pipe(takeUntil(this.destroy$), map(([optionData, includeAll]) => {
            return includeAll ? [...[this.allOption], ...optionData] : optionData;
        }));
    }
    setupUserChangeEvent() {
        this.registerEvent('change', this.inputElement, this.handleUserChange.bind(this));
        this.registerEvent('click', this.listBox, this.handleOptionClick.bind(this));
        this.registerEvent('click', this.displayBox, this.handleSelectedOptionClick.bind(this)); // TODO: this handler only needs to be attached for multi select.
    }
    handleUserChange(event) {
        const oldValue = this._value;
        let newValue = this.getSelectedValuesFromElement();
        if (!this.multiSelect) {
            newValue = newValue[0];
        }
        else {
            newValue = this.multiSelectValueType === 'CSV' ? newValue.join(this._multiSelectDelimiter) : newValue;
        }
        this.userChange(newValue, oldValue, event);
    }
    defaultUserChangeEvent(newValue, oldValue, event, renderValueOnUserChange = false, additionalDisplayValues) {
        let newDisplayValue = '';
        if (!this.multiSelect && this.record && this.displayColumnName && newValue) {
            const selectedOption = this.optionByValueGet(newValue);
            newDisplayValue = selectedOption ?
                Array.isArray(this.optionDisplayKey) ? selectedOption[this.optionDisplayKey[0]] :
                    this.getDisplayText(selectedOption)
                : newValue;
        }
        let additional;
        if (this.displayColumnName) {
            additional = { [this.displayColumnName]: newDisplayValue };
        }
        super.defaultUserChangeEvent(newValue, oldValue, event, renderValueOnUserChange, additional);
    }
    getSelectedValuesFromElement() {
        const selected = [];
        if (this.useNativeSelect) {
            if (this.multiSelect) {
                Array.from(this.inputElement.selectedOptions).forEach(o => selected.push(o.value));
            }
            else {
                selected.push(this.inputElement.value);
            }
        }
        else {
            if (this.multiSelect) {
                const bubbles = this.displayBox.querySelectorAll('.select-bubble');
                bubbles.forEach(bubble => {
                    selected.push((bubble.getAttribute('data-value') || ''));
                });
            }
            else {
                selected.push((this.displayBox.getAttribute('data-value') || ''));
            }
        }
        return selected;
    }
    /**
     * The inputElement value in this component must correspond to the html option in the select.
     * So we sanitize our stored value
     * @protected
     */
    renderValue() {
        this.renderSelectValue(this.value);
        // Update the custom display element
        this.renderCustomSelectValue(this.value);
    }
    renderSelectValue(value) {
        if (this.inputElement) {
            if (this.multiSelect) {
                const selectedOptions = typeof value === 'string' ? value.split(this._multiSelectDelimiter) : (value || []);
                // Iterate over options and set 'selected' as appropriate
                Array.from(this.inputElement.options).forEach(option => {
                    option.selected = selectedOptions.includes(option.value);
                });
            }
            else {
                this.inputElement.value = this.sanitizeOptionDataValue(this.value.toString() || '');
            }
        }
    }
    clearAutoCompleteOptionData() {
        const selectedOptions = typeof this.value === 'string' ? this.value.split(this._multiSelectDelimiter) : (this.value || []);
        this.propertySet('optionData', this.optionData.filter((option) => {
            return selectedOptions.indexOf(option[this.optionValueKey]) > -1;
        }));
        if (this.optionData.length) {
            this.optionData.forEach((option) => {
                this.allOptionsById[option._id] = option;
            });
        }
        if (this.showAutoCompleteNoResults) {
            if (this.staticOptionData.find((op) => op.value === '_notfound')) {
                this.staticOptionData = this.staticOptionData.filter((op) => op.value !== '_notfound');
            }
        }
    }
    mergeAutoCompleteOptionDataWithResponse(optionData) {
        if (this.showAutoCompleteNoResults
            && !optionData.length) {
            if (!this.staticOptionData.find((op) => op.value === '_notfound')) {
                this.staticOptionData = [...this.staticOptionData, ...[{ value: '_notfound', _disabled: 'Y', display: 'No results found' }]];
            }
        }
        else if (this.showAutoCompleteNoResults) {
            if (this.staticOptionData.find((op) => op.value === '_notfound')) {
                this.staticOptionData = this.staticOptionData.filter((op) => op.value !== '_notfound');
            }
        }
        const selectedOptions = typeof this.value === 'string' ? this.value.split(this._multiSelectDelimiter) : (this.value || []);
        optionData = optionData.filter((option) => {
            return selectedOptions.indexOf(option[this.optionValueKey]) === -1;
        });
        optionData = [...this.optionData, ...optionData];
        return optionData;
    }
    renderCustomSelectValue(value) {
        if (this.displayBox && this.displayBoxText) {
            if (this.multiSelect) {
                const selectedOptions = typeof value === 'string' ? value.split(this._multiSelectDelimiter) : (value || []);
                this.displayBox.textContent = '';
                selectedOptions.forEach(val => {
                    const selectedOption = this.optionByValueGet(val);
                    if (!selectedOption && !val) {
                        // dont render blank string as selected if its not an option in the list
                        return;
                    }
                    const displayText = selectedOption ? this.getDisplayText(selectedOption) : val; // no displayValue allowed when using multi select. options must be present or else we show the actual value.
                    this.renderSelectBubble(val, displayText);
                });
            }
            else {
                const selectedOption = this.optionByValueGet(value.toString());
                const displayText = selectedOption ? this.getDisplayText(selectedOption) : this.displayValueGet();
                this.displayBox.setAttribute('data-value', value.toString());
                this.displayBoxText.textContent = displayText;
            }
        }
    }
    renderSelectBubble(value, displayText) {
        const bubble = document.createElement('span');
        bubble.classList.add('select-bubble');
        bubble.textContent = displayText;
        bubble.setAttribute('data-value', value);
        // Create "X" icon
        const removeIcon = DIContainer.getInstance('IconManager').getIconElement('close');
        removeIcon.classList.add('select-bubble-remove');
        bubble.appendChild(removeIcon);
        this.displayBox.appendChild(bubble);
    }
    optionByValueGet(value) {
        if (typeof value === 'undefined') {
            return;
        }
        let option = this._optionsByValue[value.trim()] || this.getOptionById(value);
        if (option instanceof DataRecord) {
            option = option.data;
        }
        return option;
    }
    /**
     * Called when multiSelect property changes, updates the inputElement to allow or disallow multiSelect and then renders the value.
     * @protected
     */
    renderMultiSelect() {
        if (this.inputElement) {
            this.inputElement.multiple = this.multiSelect;
            this.renderSelectValue(this._value);
        }
        if (this.customSelectElement) {
            if (this.multiSelect) {
                this.customSelectElement.classList.add('multiple');
                this.rootElement.classList.add('multiple');
            }
            else {
                this.customSelectElement.classList.remove('multiple');
                this.rootElement.classList.remove('multiple');
            }
            if (this.multiSelect && this._multiSelectDragToSort) {
                this.setupDrag();
            }
            else if (this.dragEventIds) {
                this.unRegisterEvent(this.dragEventIds.touchstart);
                this.unRegisterEvent(this.dragEventIds.mousedown);
                this.dragEventIds = undefined;
            }
            this.renderCustomSelectValue(this._value);
        }
    }
    setupDrag() {
        const dragManager = DIContainer.getInstance('DragManager');
        if (this.dragEventIds) {
            this.unRegisterEvent(this.dragEventIds.touchstart);
            this.unRegisterEvent(this.dragEventIds.mousedown);
            this.dragEventIds = undefined;
        }
        this.dragEventIds = dragManager.setupMouseDownEvent(this, this.displayBox, {
            dragContainer: this.displayBox,
            dragType: 'sort',
            displayGhostOnDrag: true,
            previewSorting: true,
            sortType: 'horizontal',
            siblingThreshold: 10,
            siblingTargetSelector: '.select-bubble'
            // ,containerTargetSelector: ''
            ,
            dragElement: (e, _dragInfo) => {
                if (this.displayBox?.hasAttribute('disabled')) {
                    return;
                }
                const targetElement = e.target;
                if (targetElement.classList.contains('select-bubble')) {
                    return targetElement;
                }
                else if (targetElement.parentElement && targetElement.parentElement.classList.contains('select-bubble')) {
                    return targetElement.parentElement;
                }
                return;
            },
            dropDelegate: (e, dragInfo) => {
                const targetElement = dragInfo.dragElement;
                if (!targetElement)
                    return;
                this.handleUserChange(e);
            }
        });
    }
    formatStaticOptionData(option) {
        if (!Reflect.has(option, 'value')) {
            option.value = option[this.optionValueKey] || '';
        }
        option[this.optionValueKey] = option.value;
        option._static = true;
        // we will not set the display columns on static options. The issue we run into is that a display column might be the same as the value column ie. companyno. 
        // but a static options value might be '' and need to display ALL, or something. 
        /*if (Reflect.has(option, 'display')) {
            if (typeof this.optionDisplayKey === 'string') {
                option[this.optionDisplayKey] = option.display;
            } else {
                option[this.optionDisplayKey[0]] = option.display;
            }
        }*/
        return option;
    }
    /**
     * Render the option data for the dropdown.
     * @param optionData An array of objects representing the options.
     */
    renderOptionData(optionData) {
        this._optionsByValue = {};
        optionData.forEach(option => {
            const value = this.sanitizeOptionDataValue(option[this.optionValueKey]);
            this._optionsByValue[value] = option;
        });
        this.renderOptionDataStart$.next();
        let reposition = true;
        // Clear existing options
        this.renderOptionStreamGet$(optionData)
            .pipe(takeUntil(this.destroy$), takeUntil(this.renderOptionDataStart$))
            .subscribe((filteredOptionData) => {
            this.clearOptions();
            if (this.useNativeSelect) {
                // Create and append new options
                filteredOptionData.forEach((optionData) => {
                    const option = document.createElement('option');
                    const optionValue = this.sanitizeOptionDataValue(optionData[this.optionValueKey]);
                    if (optionValue === '\\-') {
                        option.text = '────────────';
                        option.disabled = true;
                        option.classList.add('optionSeparator');
                    }
                    else {
                        option.value = optionValue;
                        option.text = this.getDisplayText(optionData);
                        if (optionData._disabled === 'Y') {
                            option.disabled = true;
                        }
                    }
                    this.inputElement?.appendChild(option);
                });
            }
            else {
                // Render new custom options
                if (!optionData.length && !this.allowFreeFormEntry) {
                    this.optionsContainer.style.display = 'none';
                }
                else {
                    this.optionsContainer.style.display = '';
                }
                filteredOptionData.forEach((optionData) => {
                    const optionValue = this.sanitizeOptionDataValue(optionData[this.optionValueKey]);
                    if (this.autoCompleteFlag && !this.showAutoCompleteSelectedOptions) {
                        const selectedOptions = typeof this.value === 'string' ? this.value.split(this._multiSelectDelimiter) : (this.value || []);
                        if (selectedOptions.indexOf(optionValue) > -1) {
                            return;
                        }
                    }
                    if (optionValue === '\\-') {
                        const separator = document.createElement('div');
                        separator.classList.add('customSelectSeparator');
                        this.optionsContainer.appendChild(separator);
                        return;
                    }
                    const customOption = document.createElement('div');
                    customOption.classList.add('customSelectOption');
                    customOption.setAttribute('role', 'option');
                    customOption.textContent = this.getDisplayText(optionData);
                    customOption.setAttribute('data-value', optionValue);
                    if (optionData._freeformvalue === 'F') {
                        customOption.classList.add('freeform');
                    }
                    if (optionData._highlight === 'Y') {
                        customOption.classList.add('highlight');
                    }
                    if (optionData._disabled === 'Y') {
                        customOption.classList.add('disabled');
                    }
                    if (this.allowDeleteOptionData) {
                        const icon = DIContainer.getInstance('IconManager').getIconElement('close');
                        icon.classList.add('customSelectOptionDeleteIcon');
                        customOption.appendChild(icon);
                        customOption.classList.add('flexOption');
                    }
                    this.optionsContainer.appendChild(customOption);
                });
                if (reposition) {
                    reposition = false;
                    if (this.listBoxExpanded) {
                        this.positionListBox();
                    }
                }
            }
            this.renderValue();
        });
    }
    redrawList() {
        this.rebuildOptionDataStream$.next();
    }
    renderOptionStreamGet$(optionData) {
        return this.rebuildOptionDataStream$.pipe(startWith(false), switchMap(() => {
            return this.filteringUpdate$.pipe(startWith(this.autoCompleteValue)
            // Only apply debounceTime when filtering is active
            , RxDebounce(() => this.isFilteringActive() ? timer(50) : of(0)), map((filterValue) => {
                this.filterValue = filterValue = filterValue.trim();
                const filteredOptionData = this.filterOptionData(optionData);
                if (this.autoCompleteValue !== this.filterValue) {
                    this.autoCompleteValue = this.filterValue;
                }
                // If allowFreeFormEntry is true and the filterValue does not exactly match an option
                if (filterValue && this._allowFreeFormEntry && !filteredOptionData.find(option => option[this.optionValueKey].toLowerCase() === filterValue.toLowerCase())) {
                    // Add the freeForm value as an option
                    //let displayKey = typeof this._optionDisplayKey === 'string' ? this.optionDisplayKey as string : this.optionDisplayKey[0]
                    filteredOptionData.push({
                        [this.optionValueKey]: filterValue,
                        display: `${this.CaptionManager.getCaption('system_add_caption')}: ${filterValue}`,
                        _freeformvalue: 'F'
                    });
                }
                return filteredOptionData;
            }));
        }), switchMap((filteredOptionData) => {
            // when they flip between native and regular (if that would even happen), we will need to redraw the options
            return this.propertyGet$('useNativeSelect')
                .pipe(map(() => {
                return filteredOptionData;
            }));
        }));
    }
    isFilteringActive() {
        if (this.useNativeSelect) {
            return false;
        }
        if (this.filterValue && !this.autoCompleteFlag) {
            return true;
        }
        return false;
    }
    filterOptionData(optionData) {
        if (!this.isFilteringActive()) {
            if (this._allowFreeFormEntry && this.autoCompleteFlag) {
                return [...optionData]; // pass a new array back so when we add our "Add: <freeform value>" option it doesnt go into the actual option data array
            }
            return optionData;
        }
        const filterValue = this.filterValue.toLowerCase();
        return optionData.filter((option) => {
            const value = (option[this.optionValueKey] || '').toLowerCase();
            const display = this.getDisplayText(option)?.toLowerCase() || '';
            // Check if the filterValue is contained in either the value or display text
            return value.includes(filterValue) || display.includes(filterValue);
        });
    }
    /**
     * Get the display text for an option based on the optionDisplayKey.
     * @param optionDataElement The data object for the option.
     * @returns The display text for the option.
     */
    getDisplayText(optionDataElement) {
        if ((optionDataElement._static || optionDataElement._freeformvalue === 'F') && Reflect.has(optionDataElement, 'display')) {
            return optionDataElement.display;
        }
        if (Array.isArray(this.optionDisplayKey) && optionDataElement._freeformvalue !== 'Y') {
            // If optionDisplayKey is an array, concatenate the values with a separator
            return this.optionDisplayKey.map((key) => optionDataElement[key]).filter((value) => !!value).join(' - ');
        }
        else if (typeof this.optionDisplayKey === 'string') {
            // If optionDisplayKey is a string, use it as a single key
            if (typeof optionDataElement[this.optionDisplayKey] === 'undefined') {
                return optionDataElement[this.optionValueKey] || '';
            }
            else {
                return optionDataElement[this.optionDisplayKey];
            }
        }
        else {
            // Return an empty string if optionDisplayKey is not properly configured
            return optionDataElement[this.optionValueKey] || '';
        }
    }
    /**
     * Clear all existing options from the select input.
     */
    clearOptions() {
        while (this.inputElement?.firstChild) {
            this.inputElement.removeChild(this.inputElement.firstChild);
        }
        this.clearCustomOptions();
    }
    clearCustomOptions() {
        while (this.optionsContainer?.firstChild) {
            this.optionsContainer.removeChild(this.optionsContainer.firstChild);
        }
        this.optionsContainer.style.display = 'none';
    }
    inputTemplate() {
        this.inputElement = document.createElement('select');
        this.inputElement.classList.add('fieldInput');
        // TODO: create the option list box? Or we can just create it when rendering optionData maybe and remove it when clearing Option data? either way. 
        // it will need to also contain the filter input and stuff so probably may as well create it here. 
        this.customSelectInit();
        if (!this.useNativeSelect) {
            this.inputElement.classList.add('d-none');
        }
        else {
            this.customSelectElement.classList.add('d-none');
        }
        // Append both elements to the root
        this.fieldInputWrapper?.appendChild(this.inputElement);
        this.fieldInputWrapper?.appendChild(this.customSelectElement);
    }
    customSelectInit() {
        this.customSelectElement = document.createElement('div');
        this.customSelectElement.classList.add('customSelect');
        const displayElement = document.createElement('div');
        displayElement.classList.add('customSelectDisplayText', 'fieldInput');
        displayElement.tabIndex = 0; // Make it focusable
        this.customSelectElement.appendChild(displayElement);
        const displayElementText = document.createElement('div');
        displayElementText.classList.add('selectionText');
        displayElement.appendChild(displayElementText);
        const icon = DIContainer.getInstance('IconManager').getIconElement('expand_more');
        icon.classList.add('customSelectArrowIcon');
        //this.customSelectElement.appendChild(icon);
        displayElement.appendChild(icon);
        const listBox = document.createElement('div');
        listBox.classList.add('customSelectListBox');
        listBox.classList.add(`s360${this.componentName}ListBox`);
        listBox.setAttribute('role', 'listbox');
        listBox.setAttribute('data-global-id', this.globalId);
        listBox.tabIndex = -1; // The list box itself is not focusable but its items are
        if (isMobile()) {
            const header = document.createElement('div');
            header.classList.add('customSelectSheetHeader');
            const closeBtn = DIContainer
                .getInstance('IconManager')
                .getIconElement('close');
            closeBtn.classList.add('customSelectSheetClose');
            this.registerEvent('click', closeBtn, () => {
                this.hideListBox();
            });
            header.appendChild(closeBtn);
            listBox.appendChild(header);
        }
        // Optionally, create a search/filter input inside the list box
        const searchInput = document.createElement('input');
        searchInput.classList.add('customSelectFilter');
        searchInput.type = 'text';
        searchInput.placeholder = this.CaptionManager.getCaption('system_quicksearch_placeholder') || 'Search...';
        listBox.appendChild(searchInput);
        const optionsContainer = document.createElement('div');
        optionsContainer.classList.add('customSelectOptionsContainer');
        listBox.appendChild(optionsContainer);
        optionsContainer.style.display = 'none';
        // Append the list box to the custom select element
        // this.customSelectElement.appendChild(listBox);
        // needs to be at document level so parent containers dont hide the dropdown result
        // document.body.appendChild(listBox); // we inject it into the DOM only once someone clicks the dropdown, and then we remove it again. 
        // Event listener for click to open dropdown 
        this.registerEvent('click', this.customSelectElement, () => {
            if (!this.listBoxExpanded) {
                this.displayListBox();
            }
            else {
                this.hideListBox();
            }
        });
        // Event listener for search/filter functionality
        this.registerEvent('input', searchInput, (event) => {
            // if (this.autoCompleteFlag) {
            // 	this.autoCompleteValue = (event.target as HTMLInputElement).value; 
            // } else {
            this.filteringUpdate$.next(event.target.value);
            // }
        });
        // Store reference to the list box for future use
        this.optionsContainer = optionsContainer;
        this.filterInput = searchInput;
        this.listBox = listBox;
        this.displayBox = displayElement;
        this.displayBoxText = displayElementText;
    }
    handleFreeFormOptionAdd(selectedOptionValue) {
        const displayKey = typeof this._optionDisplayKey === 'string' ? this.optionDisplayKey : this.optionDisplayKey[0];
        const freeFormOption = {
            [this.optionValueKey]: selectedOptionValue,
            [displayKey]: selectedOptionValue,
            _freeformvalue: 'Y'
        };
        this.addOptionId(freeFormOption);
        this.allOptionsById[freeFormOption._id] = freeFormOption;
        this._optionsByValue[selectedOptionValue] = freeFormOption;
        this.optionData.push(freeFormOption);
        this.optionData = this.propertyGet('optionData');
        return of(freeFormOption);
    }
    handleOptionClick(event) {
        const target = event.target;
        // Ensure that the click is on an option element
        if (target && target.classList.contains('customSelectOption')) {
            // if (this.filterInput && this.filterInput.value) {
            // 	this.filterInput.value = '';
            // 	this.filteringUpdate$.next('');
            // }
            if (target.classList.contains('disabled')) {
                return;
            }
            let selectedOptionValue = target.getAttribute('data-value') || '';
            let obs;
            if (target.classList.contains('freeform')) {
                obs = this.handleFreeFormOptionAdd(selectedOptionValue)
                    .pipe(tap((selectedOption) => {
                    selectedOptionValue = selectedOption[this.optionValueKey];
                }));
            }
            else {
                obs = of(this.optionByValueGet(selectedOptionValue));
            }
            if (!this.multiSelect) {
                this.hideListBox(); // dont hide in multi select mode.
                setTimeout(this.focus.bind(this), 0); // focus back on the input so they can continue using tabs to navigate a form 
            }
            obs.subscribe(() => {
                if (this.multiSelect) {
                    let selected = this.getSelectedValuesFromElement();
                    const selectedOptionIndex = selected.indexOf(selectedOptionValue);
                    if (selectedOptionIndex > -1) {
                        selected.splice(selectedOptionIndex, 1);
                        target.classList.remove('selected');
                    }
                    else {
                        if (selectedOptionValue === this.allValue) {
                            selected.length = 0;
                            selected.push(selectedOptionValue);
                        }
                        else {
                            selected = selected.filter((v) => v !== this.allValue);
                            selected.push(selectedOptionValue);
                        }
                        target.classList.add('selected');
                    }
                    this.renderCustomSelectValue(selected);
                }
                else {
                    this.renderCustomSelectValue(selectedOptionValue);
                }
                this.handleUserChange(event);
                this.trigger('optionSelected', {
                    defaultEventHandler: this.handleOptionSelected.bind(this)
                }, this.optionByValueGet(selectedOptionValue));
            });
            // if (this.filterInput && this.filterInput.value) {
            // 	this.filterInput.value = '';
            // 	this.filteringUpdate$.next('');
            // }
        }
        else if (target && target.classList.contains('customSelectOptionDeleteIcon')) {
            this.handleOptionDelete(target.parentElement);
        }
    }
    handleOptionSelected(optionData) {
        // mainly a hook for specialized components
    }
    handleOptionDelete(optionElement) {
        const optionValue = optionElement.getAttribute('data-value') || '';
        if (optionValue) {
            // Remove the option from the optionData array
            this.optionData = this.optionData.filter(option => option[this.optionValueKey] !== optionValue);
            // If it's a multi-select and the option is selected, also remove it from the value
            if (this.multiSelect) {
                const newValue = Array.isArray(this.value) ? this.value : this.value.split(this._multiSelectDelimiter);
                const valueIndex = newValue.indexOf(optionValue);
                if (valueIndex > -1) {
                    newValue.splice(valueIndex, 1);
                    this.userChange(this.multiSelectValueType === 'CSV' ? newValue.join(this._multiSelectDelimiter) : newValue, this._value, undefined, true);
                }
            }
            else if (this.value === optionValue) {
                // For single select, clear the value if it matches the deleted option
                this.userChange('', this._value, undefined, true);
            }
        }
    }
    handleSelectedOptionClick(event) {
        const target = event.target;
        if (!this.multiSelect) {
            return;
        }
        // Ensure that the click is on an option element remove icon
        if (target && target.classList.contains('select-bubble-remove')) {
            event.stopImmediatePropagation();
            const selectedElement = target.parentElement;
            selectedElement.parentElement.removeChild(selectedElement);
            this.handleUserChange(event);
        }
    }
    displayListBox() {
        if (!this.listBox) {
            return;
        }
        if (this.displayBox?.hasAttribute('disabled')) {
            return;
        }
        this.trigger('displayListBox', {
            defaultEventHandler: () => {
                if (!this.listBox) {
                    return;
                }
                if (this.displayBox?.hasAttribute('disabled')) {
                    return;
                }
                this.highlightSelectedOptionsInListBox();
                this.listBox.style.visibility = 'hidden'; // we need the listbox in the dom and rendered before we can position.
                if (this.filterInput)
                    this.filterInput.style.display = 'none';
                (document.fullscreenElement || document.body).appendChild(this.listBox); // Append to the body when displaying
                this.customSelectElement.classList.add('expanded');
                this.listBox.classList.add('expanded');
                this.positionListBox(); // the positioning also removes the visibility hidden condition
                if (!isMobile()) {
                    this.filterInput?.focus();
                }
                this._mouseOverEventId = this.registerEvent('mouseover', this.listBox, this.handleMouseOver.bind(this));
                // debounced so that the click action that opened the list box doesnt immediately trigger clickAway
                debounce(() => {
                    if (!this._clickAwayEventId) {
                        this._clickAwayEventId = this.registerEvent('click', document, this.clickAway.bind(this), { capture: true });
                    }
                }, 1)();
                this.listBoxExpanded = true;
            }
        });
    }
    handleMouseOver(event) {
        const target = event.target;
        if (target && target.classList.contains('customSelectOption') && !target.classList.contains('active')) {
            this.clearActive();
            target.classList.add('active');
        }
    }
    hideListBox() {
        this.customSelectElement.classList.remove('expanded');
        this.clearActive();
        if (this.listBox) {
            this.listBox.classList.remove('expanded');
            if (this.listBox.parentNode) {
                this.listBox.parentNode.removeChild(this.listBox); // Remove from the DOM when hiding
            }
        }
        if (this._clickAwayEventId) {
            this.unRegisterEvent(this._clickAwayEventId);
            this._clickAwayEventId = '';
        }
        if (this._mouseOverEventId) {
            this.unRegisterEvent(this._mouseOverEventId);
            this._mouseOverEventId = '';
        }
        this.listBoxExpanded = false;
        if (this.filterInput && this.filterInput.value) {
            this.filterInput.value = '';
            this.filteringUpdate$.next('');
        }
    }
    focus() {
        if (this.useNativeSelect) {
            super.focus();
        }
        else {
            this.displayBox?.focus();
        }
    }
    blur() {
        if (this.useNativeSelect) {
            super.blur();
        }
        else {
            this.displayBox?.blur();
        }
    }
    positionListBox() {
        if (!this.listBox) {
            return;
        }
        // Mobile uses a full-viewport bottom sheet from Select.m.scss; desktop anchor math shifts it off-screen. Task #US62753736.
        if (isMobile()) {
            this.listBox.style.removeProperty('top');
            this.listBox.style.removeProperty('bottom');
            this.listBox.style.removeProperty('left');
            this.listBox.style.removeProperty('right');
            this.listBox.style.removeProperty('width');
            this.listBox.style.removeProperty('max-width');
            this.listBox.style.removeProperty('visibility');
            if (this.filterInput) {
                this.filterInput.style.removeProperty('width');
                this.filterInput.style.removeProperty('display');
            }
            return;
        }
        const rect = this.customSelectElement.getBoundingClientRect();
        const maxWidth = 300;
        const bottomOverflow = rect.bottom + window.scrollY + this.listBox.offsetHeight > window.innerHeight; // determines whether the list box will overflow off the bottom of the screen
        this.listBox.style.removeProperty('top');
        if (bottomOverflow) {
            // Calculate the bottom position relative to the viewport
            const topPosition = rect.top + window.scrollY;
            this.listBox.style.bottom = `${window.innerHeight - topPosition}px`;
            this.listBox.style.top = '';
        }
        else {
            this.listBox.style.top = `${rect.bottom + window.scrollY}px`; // Position below the custom select
            this.listBox.style.bottom = '';
        }
        this.listBox.classList.toggle('flipped', bottomOverflow);
        this.customSelectElement?.classList.toggle('flipped', bottomOverflow);
        if (rect.left + this.listBox.offsetWidth > window.innerWidth) {
            this.listBox.style.right = `${window.innerWidth - (rect.right + window.scrollX)}px`;
            this.listBox.style.removeProperty('left');
        }
        else {
            this.listBox.style.left = `${rect.left + window.scrollX}px`; // Align with the left edge
            this.listBox.style.removeProperty('right');
        }
        this.listBox.style.removeProperty('max-width');
        this.listBox.style.removeProperty('visibility');
        this.listBox.style.removeProperty('width');
        // ensure the listBox isn't smaller than the input
        if (this.listBox.offsetWidth < rect.width) {
            this.listBox.style.width = `${rect.width}px`; // Match the width of the custom select
        }
        if (this.listBox.offsetWidth > maxWidth && rect.width < maxWidth) {
            this.listBox.style.maxWidth = `${maxWidth}px`;
        }
        // set then input to match the listBox width and show it
        if (this.filterInput) {
            this.filterInput.style.width = `${this.listBox.clientWidth}px`;
            this.filterInput.style.removeProperty('display');
        }
    }
    highlightSelectedOptionsInListBox() {
        if (!this.listBox) {
            return;
        }
        let value = this._value;
        if (this.multiSelect && !Array.isArray(value)) {
            value = value.split(this._multiSelectDelimiter);
        }
        else if (!Array.isArray(value)) {
            value = [value];
        }
        this.listBox?.querySelectorAll('.selected').forEach((option) => {
            option.classList.remove('selected');
        });
        value.forEach((val) => {
            const option = this.listBox?.querySelector(`[data-value="${val}"]`);
            if (option) {
                option.classList.add('selected');
            }
        });
    }
    clickAway(event) {
        const target = event.target;
        if (this.listBox?.contains(target) || this.customSelectElement?.contains(target)) {
            return;
        }
        this.hideListBox();
    }
    clearActive() {
        if (!this.listBox || !this.listBoxExpanded) {
            return;
        }
        const items = Array.from(this.listBox.querySelectorAll('.customSelectOption.active'));
        items.forEach(item => item.classList.remove('active'));
    }
    activeItemGet() {
        if (!this.listBox || !this.listBoxExpanded) {
            return null;
        }
        const activeItem = this.listBox.querySelector('.customSelectOption.active');
        return activeItem;
    }
    selectedListItemsGet() {
        if (!this.listBox || !this.listBoxExpanded) {
            return [];
        }
        return Array.from(this.listBox.querySelectorAll('.customSelectOption.selected'));
    }
    selectActiveItem() {
        const activeItem = this.activeItemGet();
        if (activeItem) {
            activeItem.click();
        }
    }
    navigateList(direction) {
        if (!this.listBox || !this.listBoxExpanded) {
            return;
        }
        let currentItem = this.activeItemGet();
        if (!currentItem) {
            currentItem = this.selectedListItemsGet()[0];
        }
        const items = Array.from(this.listBox.querySelectorAll('.customSelectOption'));
        const newIndex = this.getNewIndex(items, direction, currentItem);
        this.updateActiveItem(items, newIndex);
    }
    getNewIndex(items, direction, currentItem) {
        const currentIndex = items.indexOf(currentItem);
        if (direction === 'up' || direction === 'down') {
            return (currentIndex + (direction === 'up' ? -1 : 1) + items.length) % items.length;
        }
        return currentIndex;
    }
    updateActiveItem(items, newIndex) {
        const newItem = items[newIndex];
        items.forEach(item => item.classList.remove('active'));
        newItem.classList.add('active');
        newItem.scrollIntoView({ block: 'nearest' });
    }
    renderDisabledState(isDisabled) {
        super.renderDisabledState(isDisabled);
        if (this.displayBox) {
            if (isDisabled) {
                this.displayBox.classList.add('disabled');
                this.displayBox.setAttribute('disabled', 'disabled');
            }
            else {
                this.displayBox.classList.remove('disabled');
                this.displayBox.removeAttribute('disabled');
            }
        }
    }
    getContextMenuItems(ctx) {
        const items = super.getContextMenuItems(ctx);
        if (this.action || this.endpoint) {
            items.push({
                caption: 'Refresh List',
                onClick: () => {
                    this.refreshData({ explicitRefresh: true, refreshCache: true });
                }
            });
        }
        return items;
    }
    destroy() {
        super.destroy();
        if (this.listBox && this.listBox.parentNode) {
            this.listBox.parentNode.removeChild(this.listBox);
        }
    }
    formDesignerPropertiesGet(properties) {
        properties = super.formDesignerPropertiesGet(properties);
        properties.multiSelectDelimiter = ({
            id: 'multiSelectDelimiter',
            value: this._multiSelectDelimiter,
            componentType: 'Input',
            componentProperties: {
                labelWidth: 'auto'
            },
            captionKey: 'formbuilder_select_multiselectdelimiter',
            propertyGroup: '',
            propertyIndex: 10,
            saveFlag: () => this._multiSelectDelimiter !== ','
        });
        properties.displayColumnName = ({
            id: 'displayColumnName',
            value: this._displayColumnName,
            componentType: 'Input',
            componentProperties: {
                disabled: !!this.compositeChild
            },
            captionKey: 'formbuilder_modalselect_displaycolumnname',
            propertyGroup: 'data',
            propertyIndex: 2.01,
            saveFlag: () => !!this._displayColumnName
        });
        properties.optionValueKey = ({
            id: 'optionValueKey',
            value: this.optionValueKey,
            componentType: 'Input' // TODO: select once we have a way to request list of columns
            ,
            componentProperties: {},
            captionKey: 'formbuilder_select_optionvaluekey',
            propertyGroup: 'data',
            propertyIndex: 4.01,
            saveFlag: true
        });
        let val = this.optionDisplayKey;
        if (isArray(val) && val.length < 2) {
            val = val.length === 1 ? val[0] : '';
        }
        properties.optionDisplayKey = ({
            id: 'optionDisplayKey',
            value: val,
            componentType: 'Select',
            componentProperties: {
                multiSelectValueType: 'ARRAY',
                allowFreeFormEntry: true,
                multiSelect: true,
                multiSelectDragToSort: true
            },
            captionKey: 'formbuilder_select_optiondisplaykey',
            propertyGroup: 'data',
            propertyIndex: 4.1,
            saveFlag: true
        });
        properties.autoCompleteFlag = ({
            id: 'autoCompleteFlag',
            value: this.autoCompleteFlag,
            componentType: 'InputCheckbox',
            componentProperties: {
                checkedValue: true,
                unCheckedValue: false
            },
            captionKey: 'formbuilder_select_autocompleteflag',
            propertyGroup: 'data',
            propertyIndex: 4.2,
            saveFlag: !!this.autoCompleteFlag
        });
        properties.useNativeSelect = ({
            id: 'useNativeSelect',
            value: this._useNativeSelect,
            componentType: 'InputCheckbox',
            componentProperties: {
                checkedValue: true,
                unCheckedValue: false
            },
            captionKey: 'formbuilder_select_usenativeselect',
            propertyGroup: '',
            saveFlag: () => this._useNativeSelect
        });
        properties.allowFreeFormEntry = ({
            id: 'allowFreeFormEntry',
            value: this._allowFreeFormEntry,
            componentType: 'InputCheckbox',
            componentProperties: {
                checkedValue: true,
                unCheckedValue: false,
                labelWidth: 'auto'
            },
            captionKey: 'formbuilder_select_allowfreeformentry',
            propertyGroup: '',
            saveFlag: () => this._allowFreeFormEntry
        });
        properties.multiSelectDragToSort = ({
            id: 'multiSelectDragToSort',
            value: this._multiSelectDragToSort,
            componentType: 'InputCheckbox',
            componentProperties: {
                checkedValue: true,
                unCheckedValue: false
            },
            captionKey: 'formbuilder_select_multiselectdragtosort',
            propertyGroup: '',
            saveFlag: () => this._multiSelectDragToSort
        });
        properties.includeAll = ({
            id: 'includeAll',
            value: this.includeAll,
            componentType: 'InputCheckbox',
            componentProperties: {
                checkedValue: true,
                unCheckedValue: false
            },
            captionKey: 'formbuilder_select_includealll',
            propertyGroup: 'data',
            propertyIndex: 4.8,
            saveFlag: this.includeAll
        });
        const staticValue = (this.staticOptionData || []).map((option) => {
            return { value: option.value, display: option.display };
        });
        properties.staticOptionData = ({
            id: 'staticOptionData',
            value: staticValue,
            componentType: 'InputGrid',
            componentProperties: {
                dataFormat: 'ARRAY',
                dbGridName: 'StaticOptionData',
                includeAddButton: true,
                includeDeleteButton: true,
                labelPosition: 'top',
                grid: {
                    autoResize: false
                }
            },
            captionKey: 'formbuilder_staticoptiondata',
            propertyGroup: 'data',
            propertyIndex: 4.81,
            saveFlag: !!staticValue.length
        });
        return properties;
    }
}
Select.VERSION = '$Revision: 111642 $';
